import Papa from 'papaparse';

export const loadCSVData = async (csvFilePath = '/Map_Data.csv') => {
  try {
    console.log('Starting CSV load from:', csvFilePath);
    
    // Load from public folder
    const response = await fetch(csvFilePath);
    if (!response.ok) {
      throw new Error(`Failed to load CSV file: ${response.status}`);
    }
    const csvText = await response.text();
    
    console.log('CSV loaded, length:', csvText.length);
    console.log('First 500 characters:', csvText.substring(0, 500));
    
    // Parse without skipping any rows initially
    const parsedData = Papa.parse(csvText, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      delimitersToGuess: [',', '\t', '|', ';']
    });

    console.log('Parse results:', {
      data: parsedData.data.length,
      errors: parsedData.errors,
      meta: parsedData.meta
    });

    if (parsedData.errors.length > 0) {
      console.warn('CSV parsing errors:', parsedData.errors);
    }

    // Log the first row to understand the structure
    if (parsedData.data.length > 0) {
      console.log('First row keys:', Object.keys(parsedData.data[0]));
      console.log('First row data:', parsedData.data[0]);
    }

    const processedLocations = parsedData.data
      .map((row, index) => {
        // Try different possible column names for SITE
        const site = row['SITE '] || row['SITE'] || row.SITE || '';
        const address = row.Address || row.address || '';
        const postcode = row.Postcode || row.postcode || '';
        const latitude = row.Latitude || row.latitude;
        const longitude = row.Longitude || row.longitude;

        console.log(`Row ${index + 1}:`, {
          site: site,
          address: address,
          postcode: postcode,
          lat: latitude,
          lng: longitude
        });

        return {
          rawRow: row, // Keep raw data for debugging
          site: typeof site === 'string' ? site.trim() : '',
          address: typeof address === 'string' ? address.trim() : '',
          postcode: typeof postcode === 'string' ? postcode.trim() : '',
          latitude: latitude,
          longitude: longitude
        };
      })
      .filter((item, index) => {
        const { site, address, postcode } = item;
        
        // More lenient filtering - only skip completely empty rows
        if (!site && !address && !postcode) {
          console.log(`Skipping row ${index + 1}: Completely empty`);
          return false;
        }
        
        // Skip obvious header rows or invalid data
        if (site && (site.toLowerCase() === 'site' || site.toLowerCase().includes('head office'))) {
          console.log(`Skipping row ${index + 1}: ${site} (Header or Head Office)`);
          return false;
        }
        
        return true;
      })
      .map((item, index) => {
        const { rawRow, site, address, postcode } = item;
        
        // Get coordinates from CSV, with fallback to Birmingham if invalid
        let coordinates = [52.4862, -1.8904]; // Default to Birmingham
        
        const lat = parseFloat(item.latitude);
        const lng = parseFloat(item.longitude);
        
        if (!isNaN(lat) && !isNaN(lng) && 
            lat >= -90 && lat <= 90 && 
            lng >= -180 && lng <= 180) {
          coordinates = [lat, lng];
          console.log(`Valid coordinates for ${site}: [${lat}, ${lng}]`);
        } else {
          console.warn(`Using default coordinates for ${site}: Invalid lat/lng (${item.latitude}, ${item.longitude})`);
        }

        // Some addresses in the CSV omit the postcode, so append it when missing
        const fullAddress = address && postcode && !address.toUpperCase().includes(postcode.toUpperCase())
          ? `${address.replace(/,\s*$/, '')}, ${postcode}`
          : address;

        // Excel exports can prefix phone numbers with ' to keep the leading zero
        const phone = (rawRow.Landline ?? rawRow.landline ?? '').toString().trim().replace(/^'/, '');

        return {
          id: index,
          site: site || `Location ${index + 1}`,
          operatingName: (rawRow['Operating name'] || '').toString().trim(),
          address: fullAddress || 'Address not provided',
          postcode: postcode || 'Postcode not provided',
          coordinates,
          phone,
          isNew: false, // Remove the arbitrary "new" logic for now
          hours: {
            weekdays: rawRow['Mon-Fri']?.toString().trim() || '',
            saturday: rawRow.Saturdays?.toString().trim() || rawRow.Saturday?.toString().trim() || '',
            sunday: rawRow.Sundays?.toString().trim() || rawRow.Sunday?.toString().trim() || ''
          },
          email: {
            retail: rawRow['Email for retail quotes']?.toString().trim() || '',
            parts: rawRow.Parts?.toString().trim() || '',
            accounts: rawRow['Accounts queries']?.toString().trim() || ''
          },
          website: rawRow.Web?.toString().trim() || rawRow.website?.toString().trim() || '',
          services: {
            bsiKitemark: (rawRow['BSI Kitemark'] || '').toString().toLowerCase() === 'yes',
            carVehicles: (rawRow['Car sized vehicles '] || rawRow['Car sized vehicles'] || '').toString().toLowerCase() === 'yes',
            mediumVans: (rawRow['Medium wheel-base vans (low roof)'] || '').toString().toLowerCase() === 'yes',
            largeVans: (rawRow['High roof long-wheel base vans'] || '').toString().toLowerCase() === 'yes',
            diagnosticScans: (rawRow['In house Diagnostic scans'] || '').toString().toLowerCase() === 'yes',
            adasCalibrations: (rawRow['ADAS Calibrations'] || '').toString().toLowerCase() === 'yes',
            wheelAlignment: (rawRow['GEO & Wheel Alignment'] || '').toString().toLowerCase() === 'yes',
            alloyRepairs: (rawRow['Diamond cut alloy wheel repairs'] || '').toString().toLowerCase() === 'yes'
          },
          facilities: {
            sprayBooths: parseFloat(rawRow['Number of Spray booths']) || 0,
            totalBays: parseFloat(rawRow['Total Bays - Booths and ADAS']) || 0
          },
          courtesyCars: [
            rawRow['Courtesy car (1)'],
            rawRow['Courtesy car (2)'],
            rawRow['Courtesy car (3)'],
            rawRow['Courtesy car (4)']
          ].filter(car => car && car.toString().trim()).map(car => car.toString().trim())
        };
      });

    console.log(`Processed ${processedLocations.length} locations`);
    console.log('Final processed locations:', processedLocations.map(loc => ({
      id: loc.id,
      site: loc.site,
      coordinates: loc.coordinates,
      postcode: loc.postcode
    })));
    
    return processedLocations;
  } catch (error) {
    console.error('Error loading CSV data:', error);
    throw error;
  }
};

// These functions are now primarily for backwards compatibility or manual coordinate additions
export const addPostcodeCoordinate = (postcode, coordinates) => {
  console.warn('addPostcodeCoordinate is deprecated - coordinates should come from CSV');
};

export const getPostcodeCoordinates = () => {
  console.warn('getPostcodeCoordinates is deprecated - coordinates now come from CSV');
  return {};
};